import Head from 'next/head';
import { Button, Grid, makeStyles } from '@material-ui/core';
import { Pill, StatelessSelector } from '@care/react-component-lib';

import { useAppDispatch, useSeekerHKState } from '@/components/AppState';
import Header from '@/components/Header';
import useEnterKey from '@/components/hooks/useEnterKey';
import useNextRoute from '@/components/hooks/useNextRoute';
import { useVhpMfeSharedData } from '@/components/hooks/useVhpMfeSharedData';
import { HK_CLEANING_TYPE } from '@/types/seekerHK';
import { AnalyticsHelper } from '@/utilities/analyticsHelper';

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  pill: {
    marginBottom: theme.spacing(2),
    padding: '0 !important',
  },
  nextButtonContainer: {
    padding: theme.spacing(2, 0),
  },
}));

const cleaningTypeOptions = [
  {
    label: 'Standard',
    value: HK_CLEANING_TYPE.STANDARD,
    description:
      'Maintains your home’s cleanliness with the type of cleaning you do daily or weekly.',
  },
  {
    label: 'Deep',
    value: HK_CLEANING_TYPE.DEEP,
    description: 'A thorough and more rigorous cleaning you do a few times a year.',
  },
  {
    label: 'Move out',
    value: HK_CLEANING_TYPE.MOVE_OUT,
    description: 'A deep clean of the entire house, including carpets and all surfaces. ',
  },
];

const HousekeepingCleaningTypePage = () => {
  const classes = useStyles();
  const { cleaningType } = useSeekerHKState();
  const dispatch = useAppDispatch();
  const { pushNextRoute } = useNextRoute();
  useVhpMfeSharedData();

  const onChangeHandler = (value: HK_CLEANING_TYPE[]) => {
    if (!value.length) return;
    const selection = value[0];

    dispatch({ type: 'setCleaningType', cleaningType: selection });
  };

  const handleNext = () => {
    const selectedPill = cleaningTypeOptions.find((currentPill) => {
      return currentPill.value === cleaningType;
    });

    AnalyticsHelper.logEvent({
      name: 'Member Enrolled',
      data: {
        cta_text: 'Next',
        final_step: false,
        enrollment_step: 'Type',
        cleaning_type: selectedPill?.label,
      },
    });
    pushNextRoute();
  };

  useEnterKey(true, () => handleNext(), true);

  return (
    <>
      <Head>
        <title>What type of cleaning are you interested in?</title>
      </Head>
      <Grid container className={classes.mainContainer}>
        <Grid container className={classes.header}>
          <Header>What type of cleaning are you interested&nbsp;in?</Header>
        </Grid>
        <Grid item xs={12}>
          <StatelessSelector single name="housekeepingCleaningType" onChange={onChangeHandler}>
            {cleaningTypeOptions.map((option) => (
              <Pill
                classes={{ root: classes.pill }}
                key={option.value}
                size="lg"
                label={option.label}
                value={option.value}
                description={option.description}
                selected={cleaningType === option.value}
              />
            ))}
          </StatelessSelector>
        </Grid>
        <Grid item xs={12} className={classes.nextButtonContainer}>
          <Button onClick={handleNext} variant="contained" color="primary" fullWidth size="large">
            Next
          </Button>
        </Grid>
      </Grid>
    </>
  );
};

export default HousekeepingCleaningTypePage;
